import { useState, type ReactNode } from "react";
import { Check, ChevronRight } from "lucide-react";
import type { Id } from "@constructionautomation/backend/convex/_generated/dataModel";

import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import { SetupProgress } from "./setup-progress";
import {
  ConfigApplyHint,
  DynamicConfigurationControls,
  LegacyConfigurationControls,
  summarizeConfiguration,
  type CreateCustomItem,
  type NamedCustomItem,
  type OrgMemberOption,
  type TemplateConfigColumn,
} from "./submittal-config-controls";

interface SubmittalSetupPanelProps {
  orgMembers: OrgMemberOption[];
  templateConfigColumns?: TemplateConfigColumn[];
  configValues?: Record<string, string>;
  selectedRowCount: number;
  submittalManager: string | null;
  managerSelected: boolean;
  designReviewTime: string;
  internalReviewTime: string;
  projectCustomItems?: NamedCustomItem[];
  projectId?: Id<"projects">;
  csiCodeCol: number;
  isProcoreTemplate: boolean;
  reviewTimesConfirmed: boolean;
  csiCodesVerified: boolean;
  createCustomItem?: CreateCustomItem;
  onSubmittalManagerChange: (managerEmail: string) => void;
  onDesignReviewTimeChange: (value: string) => void;
  onInternalReviewTimeChange: (value: string) => void;
  onConfigColumnChange?: (column: TemplateConfigColumn, value: string) => void;
  onConfirmReviewTimes: () => void;
  onVerifyCSICodes: () => void;
  /** Rendered at the end of the header row (e.g. the tracker strip). */
  trailing?: ReactNode;
  defaultOpen?: boolean;
  className?: string;
}

function SetupStepButton({
  done,
  doneLabel,
  label,
  onClick,
}: {
  done: boolean;
  doneLabel: string;
  label: string;
  onClick: () => void;
}) {
  if (done) {
    return (
      <span className="flex items-center gap-1 text-xs font-medium text-status-completed">
        <Check className="size-3.5" aria-hidden="true" />
        {doneLabel}
      </span>
    );
  }
  return (
    <Button variant="outline" size="compact" onClick={onClick}>
      {label}
    </Button>
  );
}

/**
 * Submittal setup: one header row (toggle, checklist, collapsed summary, trackers) over the
 * configuration controls, which stay collapsed until the user opens them.
 */
export function SubmittalSetupPanel({
  orgMembers,
  templateConfigColumns = [],
  configValues = {},
  selectedRowCount,
  submittalManager,
  managerSelected,
  designReviewTime,
  internalReviewTime,
  projectCustomItems = [],
  projectId,
  csiCodeCol,
  isProcoreTemplate,
  reviewTimesConfirmed,
  csiCodesVerified,
  createCustomItem,
  onSubmittalManagerChange,
  onDesignReviewTimeChange,
  onInternalReviewTimeChange,
  onConfigColumnChange,
  onConfirmReviewTimes,
  onVerifyCSICodes,
  trailing,
  defaultOpen = false,
  className,
}: SubmittalSetupPanelProps) {
  const [open, setOpen] = useState(defaultOpen);
  const hasDynamicConfig = templateConfigColumns.length > 0;
  const showConfirmReviewTimes = hasDynamicConfig
    ? templateConfigColumns.some((column) => column.type === "duration")
    : true;
  const summary = summarizeConfiguration({
    templateConfigColumns,
    configValues,
    submittalManager,
    designReviewTime,
    internalReviewTime,
  });

  return (
    <Collapsible open={open} onOpenChange={setOpen} className={cn("rounded-sm border", className)}>
      <div className="flex min-h-10 flex-wrap items-center gap-x-3 gap-y-1 px-1.5 py-1">
        <CollapsibleTrigger asChild>
          <Button variant="ghost" size="compact">
            <ChevronRight
              className={cn("transition-transform", open && "rotate-90")}
              aria-hidden="true"
            />
            Configuration
          </Button>
        </CollapsibleTrigger>
        <SetupProgress
          managerSelected={managerSelected}
          reviewsConfirmed={reviewTimesConfirmed}
          csiCodesVerified={csiCodesVerified}
          isProcoreTemplate={isProcoreTemplate}
          className="shrink-0"
        />
        {!open ? (
          <p className="hidden min-w-0 flex-1 truncate text-xs text-muted-foreground xl:block" title={summary}>
            {summary}
          </p>
        ) : null}
        {trailing ? <div className="ms-auto">{trailing}</div> : null}
      </div>

      <CollapsibleContent className="space-y-3 border-t px-3 py-3">
        {hasDynamicConfig ? (
          <DynamicConfigurationControls
            templateConfigColumns={templateConfigColumns}
            configValues={configValues}
            orgMembers={orgMembers}
            projectCustomItems={projectCustomItems}
            projectId={projectId}
            createCustomItem={createCustomItem}
            reviewTimesConfirmed={reviewTimesConfirmed}
            onConfigColumnChange={onConfigColumnChange}
          />
        ) : (
          <LegacyConfigurationControls
            orgMembers={orgMembers}
            submittalManager={submittalManager}
            designReviewTime={designReviewTime}
            internalReviewTime={internalReviewTime}
            reviewTimesConfirmed={reviewTimesConfirmed}
            onSubmittalManagerChange={onSubmittalManagerChange}
            onDesignReviewTimeChange={onDesignReviewTimeChange}
            onInternalReviewTimeChange={onInternalReviewTimeChange}
          />
        )}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <ConfigApplyHint selectedRowCount={selectedRowCount} />
          <div className="ms-auto flex flex-wrap items-center gap-3">
            {showConfirmReviewTimes ? (
              <SetupStepButton
                done={reviewTimesConfirmed}
                doneLabel="Review times confirmed"
                label="Confirm Review Times"
                onClick={onConfirmReviewTimes}
              />
            ) : null}
            {csiCodeCol >= 0 ? (
              <SetupStepButton
                done={csiCodesVerified}
                doneLabel="CSI codes verified"
                label="Verify CSI Codes"
                onClick={onVerifyCSICodes}
              />
            ) : null}
          </div>
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
}
